import React, { useState } from 'react';
import ApiClient from '../utils/ApiClient';

// Fulfillment prefixes. FBA -> Amazon-fulfilled; SFP/MER -> merchant-fulfilled.
const PREFIXES = [
    { key: 'FBA', label: 'FBA', sub: 'Fulfillment by Amazon' },
    { key: 'SFP', label: 'Seller Fulfilled Prime', sub: 'Standard-cost inventory' },
    { key: 'MER', label: 'Merchant Fulfilled', sub: 'Amazon returns' },
];

// Condition codes match ConditionTierService on the backend.
const CONDITIONS = [
    { code: 11, label: 'New' },
    { code: 1, label: 'Used - Like New' },
    { code: 2, label: 'Used - Very Good' },
    { code: 3, label: 'Used - Good' },
    { code: 4, label: 'Used - Acceptable' },
];

export default function AddAsinListings() {
    const [asinInput, setAsinInput] = useState('');
    const [parsedAsins, setParsedAsins] = useState([]);
    const [mapPrices, setMapPrices] = useState({}); // asin -> map price
    const [loadingMap, setLoadingMap] = useState(false);
    const [selectedPrefixes, setSelectedPrefixes] = useState([]);
    const [selectedConditions, setSelectedConditions] = useState([]); // condition codes
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [response, setResponse] = useState(null);

    // Parse ASINs from textarea (one per line), then fetch their MAP prices.
    const handleParseAsins = () => {
        if (!asinInput.trim()) {
            alert('Please enter at least one ASIN');
            return;
        }

        const lines = asinInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
        const uniqueAsins = [...new Set(lines)];
        const validAsins = uniqueAsins.filter(asin => /^[A-Z0-9]{10}$/i.test(asin));

        if (validAsins.length === 0) {
            alert('No valid ASINs found. ASINs must be 10 characters (letters and numbers only).');
            return;
        }

        setParsedAsins(validAsins);
        setResponse(null);

        // Fetch MAP prices for preview (prices are locked to MAP x condition multiplier).
        setLoadingMap(true);
        ApiClient.post('/api/listings/map-prices', { asins: validAsins })
            .then(res => setMapPrices(res.data?.mapPrices || {}))
            .catch(() => setMapPrices({}))
            .finally(() => setLoadingMap(false));
    };

    const togglePrefix = (key) => {
        setSelectedPrefixes(prev =>
            prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
    };

    const toggleCondition = (code) => {
        setSelectedConditions(prev =>
            prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
    };

    const handleClear = () => {
        setAsinInput('');
        setParsedAsins([]);
        setMapPrices({});
        setSelectedPrefixes([]);
        setSelectedConditions([]);
        setResponse(null);
    };

    const asinsWithMap = parsedAsins.filter(a => mapPrices[a] > 0);
    const combosPerAsin = selectedPrefixes.length * selectedConditions.length;
    const totalListings = asinsWithMap.length * combosPerAsin;
    const stepsLocked = parsedAsins.length === 0;
    const canSubmit = parsedAsins.length > 0 && selectedPrefixes.length > 0
        && selectedConditions.length > 0 && !isSubmitting;

    const handleSubmit = () => {
        if (selectedPrefixes.length === 0 || selectedConditions.length === 0) {
            alert('Select at least one prefix and one condition.');
            return;
        }

        const skipCount = parsedAsins.length - asinsWithMap.length;
        const skipNote = skipCount > 0
            ? `\n${skipCount} ASIN(s) have no MAP price and will be skipped.`
            : '';

        if (!window.confirm(
            `Create ${totalListings} listing(s) — ${asinsWithMap.length} ASIN(s) × `
            + `${selectedPrefixes.length} prefix(es) × ${selectedConditions.length} condition(s), `
            + `priced at MAP × condition multiplier.${skipNote}`)) {
            return;
        }

        setIsSubmitting(true);
        setResponse(null);

        ApiClient.post('/api/listings/create-bulk', {
            asins: parsedAsins,
            prefixes: selectedPrefixes,
            conditions: selectedConditions,
        })
            .then(res => {
                setIsSubmitting(false);
                setResponse(res.data);
            })
            .catch(error => {
                setIsSubmitting(false);
                setResponse({
                    error: true,
                    message: error.response?.data?.message || 'Error creating listings',
                    results: [],
                });
            });
    };

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Add ASIN Listings</h1>
                <p className="mt-2 text-sm text-gray-600">
                    Create Amazon listings across fulfillment prefixes and conditions. Prices are set
                    automatically to each ASIN's MAP × the condition's multiplier.
                </p>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-600">
                    <li>
                        Nothing is pre-selected: choose at least one prefix (FBA, SFP, MER) and at least one
                        condition in Step 2. One listing is created per prefix × condition you tick.
                    </li>
                    <li>
                        For SFP New only, tick just <span className="font-medium">Seller Fulfilled Prime</span> and
                        just <span className="font-medium">New</span>.
                    </li>
                    <li>ASINs without a MAP price are skipped.</li>
                    <li>
                        The automatic <span className="font-mono text-xs">sfp-listing-sweep</span> job (every 6 hours)
                        already creates SFP New listings for ASINs on open POs. Use this page for other
                        conditions or ASINs not on an open PO.
                    </li>
                </ul>
            </div>

            {/* Step 1: ASIN Input */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Enter ASINs</h2>
                <textarea
                    id="asin-input"
                    rows={8}
                    value={asinInput}
                    onChange={(e) => setAsinInput(e.target.value)}
                    placeholder="B01M3SOVIC&#10;B07XYZ1234&#10;B08ABC5678"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                    disabled={isSubmitting}
                />
                <p className="mt-2 text-xs text-gray-500">
                    One ASIN per line. ASINs must be 10 characters (letters and numbers).
                </p>
                <div className="flex gap-3 mt-4">
                    <button
                        type="button"
                        onClick={handleParseAsins}
                        disabled={isSubmitting || !asinInput.trim()}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Parse ASINs
                    </button>
                    {parsedAsins.length > 0 && (
                        <button
                            type="button"
                            onClick={handleClear}
                            disabled={isSubmitting}
                            className="inline-flex items-center rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-300 disabled:opacity-50"
                        >
                            Clear All
                        </button>
                    )}
                </div>
            </div>

            {/* Step 2: Choose prefixes & conditions */}
            <fieldset
                disabled={stepsLocked}
                aria-disabled={stepsLocked}
                className={`bg-white shadow rounded-lg p-6 mb-6 ${stepsLocked ? 'opacity-50 pointer-events-none select-none' : ''}`}
            >
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Step 2: Choose prefixes & conditions</h2>
                    {stepsLocked && <p className="text-xs text-gray-500 mb-1">Parse ASINs in Step 1 to continue.</p>}
                    <p className="text-sm text-gray-600 mb-4">
                        A listing is created for every prefix × condition combination you select.
                    </p>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Prefixes</h3>
                            <div className="space-y-2">
                                {PREFIXES.map(p => (
                                    <label key={p.key} className="flex items-start gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedPrefixes.includes(p.key)}
                                            onChange={() => togglePrefix(p.key)}
                                            disabled={isSubmitting}
                                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span>
                                            <span className="block text-sm font-medium text-gray-900">{p.label}</span>
                                            <span className="block text-xs text-gray-500">{p.sub} · {p.key}-XXXX-XXXX</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Conditions</h3>
                            <div className="space-y-2">
                                {CONDITIONS.map(c => (
                                    <label key={c.code} className="flex items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedConditions.includes(c.code)}
                                            onChange={() => toggleCondition(c.code)}
                                            disabled={isSubmitting}
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="text-sm font-medium text-gray-900">{c.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
            </fieldset>

            {/* Step 3: Review & create */}
            <fieldset
                disabled={stepsLocked}
                aria-disabled={stepsLocked}
                className={`bg-white shadow rounded-lg p-6 mb-6 ${stepsLocked ? 'opacity-50 pointer-events-none select-none' : ''}`}
            >
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Step 3: Review & create</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        {stepsLocked
                            ? 'Parse ASINs in Step 1 to see MAP prices and the listings that will be created.'
                            : loadingMap
                            ? 'Loading MAP prices…'
                            : `${asinsWithMap.length} of ${parsedAsins.length} ASIN(s) have a MAP price. `
                              + `${combosPerAsin > 0 ? `${totalListings} listing(s) will be created.` : 'Select prefixes and conditions above.'}`}
                    </p>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="py-2 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6">ASIN</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">MAP</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Listings to create</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {parsedAsins.map(asin => {
                                    const map = mapPrices[asin];
                                    const hasMap = map > 0;
                                    return (
                                        <tr key={asin} className={hasMap ? '' : 'bg-yellow-50'}>
                                            <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm font-mono text-gray-900 sm:pl-6">{asin}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                                {hasMap ? `$${Number(map).toFixed(2)}` : <span className="text-yellow-700">no MAP — will skip</span>}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">
                                                {hasMap ? combosPerAsin : 0}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!canSubmit || totalListings === 0}
                            className="inline-flex items-center rounded-md bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Creating Listings…
                                </>
                            ) : (
                                `Create ${totalListings} Listing(s) on Amazon`
                            )}
                        </button>
                    </div>
            </fieldset>

            {/* Results */}
            {response && (
                <div className={`bg-white shadow rounded-lg p-6 ${response.error ? 'border-l-4 border-red-500' : 'border-l-4 border-green-500'}`}>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Results</h2>

                    {response.error ? (
                        <div className="text-sm text-red-700 mb-4"><strong>Error:</strong> {response.message}</div>
                    ) : (
                        <div className="mb-4">
                            <div className="grid grid-cols-4 gap-4 mb-4">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-900">{response.totalItems}</div>
                                    <div className="text-sm text-blue-700">Rows</div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-green-900">{response.successCount}</div>
                                    <div className="text-sm text-green-700">Created</div>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-yellow-900">{response.skippedCount}</div>
                                    <div className="text-sm text-yellow-700">Skipped</div>
                                </div>
                                <div className="bg-red-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-red-900">{response.errorCount}</div>
                                    <div className="text-sm text-red-700">Failed</div>
                                </div>
                            </div>

                            {response.results && response.results.length > 0 && (
                                <div className="mt-4 overflow-x-auto max-h-96 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="py-2 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6">ASIN</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Prefix</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Condition</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Price</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Status</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Message</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {response.results.map((r, i) => (
                                                <tr key={i} className={r.status === 'error' ? 'bg-red-50' : r.status === 'skipped' ? 'bg-yellow-50' : 'bg-green-50'}>
                                                    <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm font-mono text-gray-900 sm:pl-6">{r.asin}</td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">{r.prefix || '—'}</td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">{r.condition || '—'}</td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">{r.price != null ? `$${Number(r.price).toFixed(2)}` : '—'}</td>
                                                    <td className="whitespace-nowrap px-3 py-2 text-sm">
                                                        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                                            r.status === 'success' ? 'bg-green-100 text-green-700'
                                                                : r.status === 'skipped' ? 'bg-yellow-100 text-yellow-700'
                                                                : 'bg-red-100 text-red-700'}`}>
                                                            {r.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-gray-700">{r.message}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setResponse(null)}
                        className="mt-4 text-sm text-indigo-600 hover:text-indigo-500 font-medium"
                    >
                        Clear Results
                    </button>
                </div>
            )}
        </div>
    );
}
